import { getDataFetchingClientRequestState } from 'marketplace-ui-common/dataFetchingClient/utils/getDataFetchingClientRequestState';
import { useParams } from 'quartz-routing/react-router';
import type {
  GetCollectionBySlugResponse,
  ListingCard as DSListingCard,
} from '../../../__generated__/chirp/com/hubspot/marketplace/discovery/studio/rpc/CollectionsRpc';
import type { CollectionWithPreviews } from '../../../__generated__/chirp/com/hubspot/marketplace/quality/rpc/CollectionsRpc';
import { useDiscoveryCollections } from '../../collections/hooks/useDiscoveryCollections';
import {
  UmbrellaCollection,
  useUmbrellaCollections,
} from '../../collections/hooks/useUmbrellaCollections';
import { useGetCollectionBySlugQuery } from './useGetCollectionBySlugQuery';

interface PublishedCollectionQueryResult {
  data: CollectionWithPreviews | undefined;
  error: Error | undefined;
  isUmbrellaCollection: false;
  loading: boolean;
}

interface UmbrellaCollectionQueryResult {
  loading: boolean;
  data: UmbrellaCollection | undefined;
  error: Error | undefined;
  isUmbrellaCollection: true;
}

type CollectionQueryResult =
  | PublishedCollectionQueryResult
  | UmbrellaCollectionQueryResult;

type UseCollectionQueryOptions =
  | { mode: 'id'; collectionId: number }
  | { mode: 'url' }
  | { mode: 'recent' };

/** Fetches collection based on URL slug or most recent collection, with umbrella collection support */
export function useCollectionQuery(
  options: UseCollectionQueryOptions = { mode: 'url' }
): CollectionQueryResult {
  const { mode } = options;
  const { collectionSlug } = useParams<{ collectionSlug: string }>();

  const umbrellaCollections = useUmbrellaCollections();
  const umbrellaCollectionsRequestState = getDataFetchingClientRequestState(
    umbrellaCollections.error,
    umbrellaCollections.loading
  );
  const umbrellaCollection =
    mode === 'url' && collectionSlug
      ? umbrellaCollections.getUmbrellaCollectionBySlug(collectionSlug)
      : undefined;

  const slugQuery = useGetCollectionBySlugQuery({
    skip: mode !== 'url' || !collectionSlug || !!umbrellaCollection,
  });
  const dsResult = useDiscoveryCollections({ skip: mode === 'url' });

  if (mode === 'url' && !collectionSlug) {
    return {
      data: undefined,
      error: undefined,
      isUmbrellaCollection: false,
      loading: false,
    };
  }

  if (umbrellaCollection && umbrellaCollectionsRequestState !== 'PENDING') {
    return {
      data: umbrellaCollection,
      error: undefined,
      isUmbrellaCollection: true,
      loading: false,
    };
  }

  if (mode === 'url') {
    if (slugQuery.loading || umbrellaCollectionsRequestState === 'PENDING') {
      return {
        data: undefined,
        error: undefined,
        isUmbrellaCollection: false,
        loading: true,
      };
    }

    const slugResult = slugQuery.data?.result;
    if (slugQuery.error || !slugResult) {
      return {
        data: undefined,
        error: new Error('Collection not found'),
        isUmbrellaCollection: false,
        loading: false,
      };
    }

    return {
      data: toCollectionWithPreviews(slugResult),
      error: undefined,
      isUmbrellaCollection: false,
      loading: false,
    };
  }

  if (dsResult.loading || umbrellaCollectionsRequestState === 'PENDING') {
    return {
      data: undefined,
      error: undefined,
      isUmbrellaCollection: false,
      loading: true,
    };
  }

  if (dsResult.error) {
    return {
      data: undefined,
      error: new Error('Failed to load collections'),
      isUmbrellaCollection: false,
      loading: false,
    };
  }

  const dsCollection =
    mode === 'recent'
      ? dsResult.collections[0]
      : dsResult.collections.find((c) => c.entityId === options.collectionId);

  if (dsCollection) {
    return {
      data: {
        id: dsCollection.entityId,
        slug: dsCollection.slug,
        name: dsCollection.name,
        title: dsCollection.title ?? undefined,
        description: dsCollection.description ?? '',
        tagline: dsCollection.description ?? '',
        itemCount: dsCollection.listingCount,
        displayed: true,
        previewItems: dsCollection.previewItems.map((item) => ({
          listingId: item.listingId,
          name: item.name,
          iconUrl: item.iconUrl,
          slug: item.slug,
        })),
        updatedAt: undefined,
        htmlPageTitle: dsCollection.title ?? '',
        metaDescription: dsCollection.description ?? '',
      } as CollectionWithPreviews,
      error: undefined,
      isUmbrellaCollection: false,
      loading: false,
    };
  }

  return {
    data: undefined,
    error: new Error(
      mode === 'recent' ? 'No collections available' : 'Collection not found'
    ),
    isUmbrellaCollection: false,
    loading: false,
  };
}

function toCollectionWithPreviews(
  result: GetCollectionBySlugResponse
): CollectionWithPreviews {
  return {
    id: result.entityId,
    slug: result.slug,
    name: result.title ?? result.slug,
    title: result.title,
    description: result.description ?? '',
    tagline: result.description ?? '',
    itemCount: result.listingCount,
    previewItems: (result.items ?? []).map((item: DSListingCard) => ({
      listingId: item.listingId,
      name: item.listingName ?? '',
      iconUrl: item.iconUrl,
      slug: item.slug,
    })),
    updatedAt: undefined,
    htmlPageTitle: result.htmlPageTitle ?? result.title ?? '',
    metaDescription: result.metaDescription ?? result.description ?? '',
  } as CollectionWithPreviews;
}
